import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { EXPENSE_CATEGORIES, fetchVendorOptions, fetchCategoryOptions, DEFAULT_VENDOR_OPTIONS } from "@/lib/expenseOptions";
import { Receipt, Plus } from "lucide-react";
import { ColonyBadge, ColonyDotName } from "@/lib/colonyColors";
import { PhotoThumb } from "@/components/PhotoLightbox";
import { MultiPhotoUploader } from "@/components/MultiPhotoUploader";
import { CalendarYearSelect } from "@/components/CalendarYearSelect";
import { useCalendarPage } from "@/lib/useCalendarPage";
import { t as T } from "@/lib/i18n";
import { ExpenseReceiptFields } from "@/components/finance/ExpenseReceiptFields";
import { ReceiptFollowupBadge, ReceiptDetailsFix, pendingMissing, AmountMismatchBadge } from "@/components/finance/ReceiptFollowup";

const LEDGER = "mgmt_usa_nj";
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Worker-manager expenses: a normal worker who the admin flagged as a
 * "worker manager" can submit expenses to the Management USA NJ books
 * and see the entries they submitted. Same audit rule as admin/driver
 * expenses: a receipt photo OR a written no-receipt reason is required.
 */
export const WorkerExpensesDialog = ({ open, onOpenChange, focusExpenseId = "" }) => {
  const opener = useRef(null);
  const expensePage = useCalendarPage({ client: api, path: `/expenses?ledger=${LEDGER}&compact=true`, enabled: open });
  const { items, setItems, loading, reload: load } = expensePage;
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: today(), category: "", vendor: "", amount: "", notes: "", no_receipt_reason: "", colony_id: "", payment_method: "", payment_reference: "" });
  const [receipts, setReceipts] = useState([]);
  const [itemPhotos, setItemPhotos] = useState([]);
  const [noReceipt, setNoReceipt] = useState(false);
  const [colonies, setColonies] = useState([]);
  const [vendorOptions, setVendorOptions] = useState(DEFAULT_VENDOR_OPTIONS);
  const [categoryOptions, setCategoryOptions] = useState(EXPENSE_CATEGORIES);

  useEffect(() => {
    if (open) {
      setAdding(false);
      api.get("/mgmt/colonies").then((r) => setColonies(r.data || [])).catch(() => {});
      fetchVendorOptions(api, LEDGER).then(setVendorOptions);
      fetchCategoryOptions(api, LEDGER).then(setCategoryOptions);
    }
  }, [open]);

  const save = async () => {
    if (saving) return;
    const amount = parseFloat(form.amount);
    if (!form.category.trim()) return toast.error("Category is required (e.g. Supplies, Gas).");
    if (noReceipt && !form.vendor.trim()) return toast.error("Vendor / store is required.");
    if (!amount) return toast.error("Enter an amount — use a minus for returns, e.g. -25.");
    if (noReceipt) {
      if (!form.no_receipt_reason.trim()) return toast.error("Write why there's no receipt.");
    } else if (!receipts.length) {
      return toast.error("Attach a receipt photo, or tick the no-receipt box and explain why.");
    }
    if (!itemPhotos.length) {
      return toast.error("Add a photo of what you bought — one photo of the full cart/wagon is fine for a big order.");
    }
    setSaving(true);
    try {
      await api.post("/expenses", {
        date: form.date,
        category: form.category.trim(),
        vendor: form.vendor.trim(),
        amount,
        notes: form.notes.trim(),
        receipts,
        item_photos: itemPhotos,
        no_receipt_reason: form.no_receipt_reason.trim(),
        colony_id: form.colony_id || null,
        payment_method: form.payment_method || "",
        payment_reference: form.payment_reference || "",
        ledger: LEDGER,
      });
      await load();
      setForm({ date: today(), category: "", vendor: "", amount: "", notes: "", no_receipt_reason: "", colony_id: "", payment_method: "", payment_reference: "" });
      setReceipts([]);
      setItemPhotos([]);
      setNoReceipt(false);
      setAdding(false);
      toast.dismiss();
      toast.success(noReceipt ? "Expense saved." : "Expense saved — reading the receipt now.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="worker-expenses-dialog"
        onOpenAutoFocus={() => { opener.current = document.activeElement; }}
        onCloseAutoFocus={event => {
          if (opener.current?.isConnected && opener.current !== document.body) {
            event.preventDefault(); opener.current.focus({ preventScroll: true });
          }
        }}>
        <DialogHeader className="pr-6">
          <div className="flex min-w-0 items-center justify-between gap-2" data-testid="worker-expenses-top-actions">
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" /> My expenses
          </DialogTitle>
          {!adding && <CalendarYearSelect page={expensePage} id="worker-expenses-year" T={T} />}
          </div>
          <DialogDescription>
            Expenses you submit go straight to the Management USA NJ books.
          </DialogDescription>
        </DialogHeader>

        {!adding && (
          <div className="space-y-3">
            {expensePage.error && <div role="alert" className="text-sm text-destructive" data-testid="worker-expenses-year-error">{expensePage.error} <Button variant="outline" size="sm" onClick={load} data-testid="worker-expenses-year-retry">{T("Retry")}</Button></div>}
            <Button size="sm" className="rounded-sm" onClick={() => setAdding(true)} data-testid="worker-expense-new-btn">
              <Plus className="w-3.5 h-3.5 mr-2" /> Add expense
            </Button>
            {loading && <p role="status" className="text-sm text-muted-foreground" data-testid="worker-expenses-loading">{T("Loading…")}</p>}
            {!loading && !expensePage.error && !items.length && (
              <p className="text-sm text-muted-foreground" data-testid="worker-expenses-empty">No expenses submitted yet.</p>
            )}
            {items.map((x) => {
              const receiptCount = typeof x.receipt_count === "number"
                ? x.receipt_count
                : (x.receipts?.length || 0) + (x.receipt_data_url ? 1 : 0);
              const itemPhotoCount = typeof x.item_photo_count === "number"
                ? x.item_photo_count
                : (x.item_photos?.length || 0);
              const loadThumbs = async () => {
                if (Array.isArray(x.receipts) && x.receipts.length && Array.isArray(x.item_photos)) return; // already have them
                try {
                  const { data } = await api.get(`/expenses/${x.id}`);
                  setItems((prev) => prev.map((it) => it.id === x.id ? { ...it, receipts: data.receipts || [], receipt_data_url: data.receipt_data_url || "", item_photos: data.item_photos || [] } : it));
                } catch (_) { toast.error(T("Could not load the photos. Please try again.")); }
              };
              return (
                <div key={x.id} className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-3 rounded-sm border border-border p-3" data-testid={`worker-expense-row-${x.id}`}>
                  <div className="min-w-0 [overflow-wrap:anywhere]">
                    <div className="font-semibold text-sm">{x.category}{x.vendor ? ` — ${x.vendor}` : pendingMissing(x).length ? "" : " — reading receipt…"}<ReceiptFollowupBadge expense={x} testId={`worker-expense-needs-details-${x.id}`} /><AmountMismatchBadge expense={x} testId={`worker-expense-amount-flag-${x.id}`} /></div>
                    <div className="text-xs text-muted-foreground">{x.date}{x.notes ? ` · ${x.notes}` : ""}</div>
                    <ReceiptDetailsFix expense={x} autoFocus={x.id === focusExpenseId} onFixed={data => setItems(prev => prev.map(it => it.id === x.id ? { ...it, ...data } : it))} />
                    {x.colony_id && (
                      <div className="mt-1 max-w-full [&>span]:max-w-full [&>span]:whitespace-normal">
                        <ColonyBadge id={x.colony_id} name={colonies.find((c) => c.id === x.colony_id)?.name || "Colony"} />
                      </div>
                    )}
                    {(x.receipts || []).length > 0 ? (
                      <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        {x.receipts.map((r, i) => (
                          <PhotoThumb key={i} src={r} photos={x.receipts} index={i} alt="receipt" size="w-10 h-10" />
                        ))}
                      </div>
                    ) : receiptCount > 0 ? (
                      <button
                        type="button"
                        onClick={loadThumbs}
                        className="mt-1.5 text-[10px] text-primary font-semibold uppercase tracking-wider hover:underline"
                        data-testid={`worker-expense-load-receipts-${x.id}`}
                      >
                        📎 View {receiptCount} receipt{receiptCount > 1 ? "s" : ""}
                      </button>
                    ) : null}
                    {(x.item_photos || []).length > 0 ? (
                      <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        {x.item_photos.map((r, i) => (
                          <PhotoThumb key={i} src={r} photos={x.item_photos} index={i} alt="item purchased" size="w-10 h-10" testid={`worker-expense-item-photo-${x.id}-${i}`} />
                        ))}
                      </div>
                    ) : itemPhotoCount > 0 ? (
                      <button
                        type="button"
                        onClick={loadThumbs}
                        className="mt-1.5 text-[10px] text-primary font-semibold uppercase tracking-wider hover:underline block"
                        data-testid={`worker-expense-load-item-photos-${x.id}`}
                      >
                        🛒 View {itemPhotoCount} item photo{itemPhotoCount > 1 ? "s" : ""}
                      </button>
                    ) : null}
                  </div>
                  <div className="whitespace-nowrap text-sm font-bold" data-testid={`worker-expense-amount-${x.id}`}>${Number(x.amount).toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        )}

        {adding && (
          <div className="space-y-3" data-testid="worker-expense-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category *</Label>
                <Combobox value={form.category} onChange={category => setForm(current => ({ ...current, category }))} options={categoryOptions} placeholder="Pick or type…" className="h-9" testId="worker-expense-category" />
              </div>
              <div>
                <Label className="text-xs">Amount ($) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0.00 (minus = return)" className="rounded-sm h-9" data-testid="worker-expense-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Details — what was this for?</Label>
              <Textarea value={form.notes} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} rows={2} data-testid="worker-expense-notes" />
            </div>
            <ExpenseReceiptFields worker form={form} setForm={setForm} receipts={receipts} onReceiptsChange={setReceipts} noReceipt={noReceipt} onNoReceiptChange={setNoReceipt} vendorOptions={vendorOptions} />
            <div>
              <Label className="text-xs">Payment method (optional)</Label>
              <Select value={form.payment_method || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v === "__none__" ? "" : v, payment_reference: "" }))}>
                <SelectTrigger className="rounded-sm h-9" data-testid="worker-expense-payment-method"><SelectValue placeholder="— not specified —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— not specified —</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Zelle">Zelle</SelectItem>
                  <SelectItem value="Credit card">Credit card</SelectItem>
                  <SelectItem value="Debit card">Debit card</SelectItem>
                  <SelectItem value="Bank transfer">Bank transfer / ACH</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(() => {
              const m = (form.payment_method || "").toLowerCase();
              const spec = m.includes("credit") || m.includes("debit")
                ? { label: "Last 4 of card", placeholder: "1234", maxLen: 4, digitsOnly: true }
                : m.includes("check")
                  ? { label: "Check #", placeholder: "1042", maxLen: 10, digitsOnly: true }
                  : m.includes("zelle")
                    ? { label: "Zelle transaction # (optional)", placeholder: "e.g. ABC123", maxLen: 32 }
                    : null;
              if (!spec) return null;
              return (
                <div>
                  <Label className="text-xs">{spec.label}</Label>
                  <Input
                    inputMode={spec.digitsOnly ? "numeric" : "text"}
                    maxLength={spec.maxLen}
                    placeholder={spec.placeholder}
                    value={form.payment_reference}
                    onChange={(e) => setForm((f) => ({ ...f, payment_reference: spec.digitsOnly ? e.target.value.replace(/\D/g, "").slice(0, spec.maxLen) : e.target.value.slice(0, spec.maxLen) }))}
                    className="rounded-sm h-9"
                    data-testid="worker-expense-payment-ref"
                  />
                </div>
              );
            })()}
            <div>
              <Label className="text-xs">Bill to colony (optional)</Label>
              <Select value={form.colony_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, colony_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="rounded-sm h-9" data-testid="worker-expense-colony"><SelectValue placeholder="— unassigned overhead —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— unassigned overhead —</SelectItem>
                  {colonies.map((c) => (
                    <SelectItem key={c.id} value={c.id}><ColonyDotName id={c.id} name={c.name} /></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
                <p className="text-xs text-muted-foreground mb-1">Got a big order? One photo of the full cart/wagon is fine instead of every single item.</p>
                <MultiPhotoUploader value={itemPhotos} onChange={setItemPhotos} label="Photos of items bought *" testId="worker-expense-item-photo" max={6} colonyId={form.colony_id || undefined} noLive />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm flex-1" onClick={() => setAdding(false)} disabled={saving} data-testid="worker-expense-cancel">
                Cancel
              </Button>
              <Button size="sm" className="rounded-sm flex-1" onClick={save} disabled={saving} data-testid="worker-expense-save">
                {saving ? "Saving…" : "Save expense"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
